import { createTheme } from '@mui/material/styles';
import fonts from '@assets/fonts';

const themeConfig = createTheme({
  palette: {
    primary: {
      main: '#5FB16D',
      700: '#8FC899',
      500: '#AFD8B6',
      200: '#CFE8D3',
      100: '#EFF7F0',
      50: '#FBFFFB',
      light: '#4A5AFF',
      dark: '#0F2725',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0F2725',
      700: '#576866',
      500: '#879392',
      200: '#CFD4D3',
      100: '#E7E9E9',
      dark: '#5768664D',
    },
    info: {
      main: '#B4BFCD',
      light: '#FFEED3',
      contrastText: '#E97216',
    },
    success: {
      main: '#39B54E',
      700: '#82D390',
      500: '#B3EFBE',
      200: '#D9FEDF',
      100: '#F6FFF8',
    },
    error: {
      main: '#EF1F1F',
      700: '#F46262',
      500: '#F78F8F',
      200: '#FCD2D2',
      100: '#FDE9E9',
    },
    warning: {
      main: '#FFBC0F',
      700: '#FFD260',
      500: '#FFE193',
      200: '#FFEDBF',
      100: '#FFFAED',
    },
    grey: {
      A100: '#94A3B8',
      700: '#B4BFCD',
      500: '#C9D1DC',
      200: '#EAEDF1',
      100: '#F4F6F8',
    },
    black: {
      A100: '#1C2639',
      700: '#2C3545',
      500: '#717A8F',
      200: '#C6CDDA',
      100: '#F0F0F3',
    },
    shadow: {
      main: '#6D9B75',
      secondary: '#50945C',
    },
    divider: '#94A3B8',
    text: {
      primary: '#1C2639',
    },
    toastText: {
      error: 'rgb(96, 30, 30)',
      info: 'rgb(38, 78, 45)',
      success: 'rgb(38, 78, 45)',
      warning: 'rgb(102, 80, 25)',
    },
    backgroundColorOpacity: {
      primary: {
        100: 'rgba(95, 177, 109, 0.1)',
        700: 'rgba(95, 177, 109, 0.3)',
        500: 'rgba(95, 177, 109, 0.5)',
      },
      secondary: {
        100: 'rgba(15, 39, 37, 0.1)',
        700: 'rgba(15, 39, 37, 0.3)',
        500: 'rgba(15, 39, 37, 0.5)',
      },
      error: {
        100: 'rgba(239, 31, 31, 0.1)',
        700: 'rgba(239, 31, 31, 0.3)',
        500: 'rgba(239, 31, 31, 0.5)',
      },
      info: {
        100: 'rgba(180, 191, 205, 0.1)',
        700: 'rgba(180, 191, 205, 0.3)',
        500: 'rgba(180, 191, 205, 0.5)',
      },
      success: {
        100: 'rgba(57, 181, 78, 0.1)',
        700: 'rgba(57, 181, 78, 0.3)',
        500: 'rgba(57, 181, 78, 0.5)',
      },
      warning: {
        100: 'rgba(255, 188, 15, 0.1)',
        700: 'rgba(255, 188, 15, 0.3)',
        500: 'rgba(255, 188, 15, 0.5)',
      },
    },
  },
  spacing: [
    0, 1, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 48, 64, 96,
    128, 256,
  ],
  typography: {
    fontFamily: 'Twemoji Country Flags, Inter',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    fontWeightExtraBold: 800,
  },
  mixins: {
    toolbar: {
      minHeight: 72,
      minWidth: '100%',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `{
        ${fonts.map((font: string) => `@font-face ${font}`).join('\n')}
      }`,
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'secondary' },
          style: {
            border: `1px solid`,
          },
        },
      ],
    },
  },
});

const scaleFontSize = (
  originalFontSize: number,
  mdCoefficient: number,
  smCoefficient: number
) => {
  return {
    fontSize: originalFontSize,
    [themeConfig.breakpoints.down('md')]: {
      fontSize: originalFontSize * mdCoefficient,
    },
    [themeConfig.breakpoints.down('sm')]: {
      fontSize: originalFontSize * smCoefficient,
    },
  };
};

themeConfig.typography.h1 = scaleFontSize(46, 0.85, 0.75);
themeConfig.typography.h2 = scaleFontSize(34, 0.85, 0.75);
themeConfig.typography.h3 = scaleFontSize(28, 0.85, 0.75);
themeConfig.typography.h4 = scaleFontSize(22, 0.85, 0.75);
themeConfig.typography.h5 = scaleFontSize(18, 0.85, 0.75);
themeConfig.typography.h6 = scaleFontSize(14, 0.85, 0.75);
themeConfig.typography.bodyBig = scaleFontSize(14, 0.85, 0.75);
themeConfig.typography.body = scaleFontSize(12, 0.85, 0.75);
themeConfig.typography.bodySmall = scaleFontSize(10, 0.85, 0.75);

export const theme = themeConfig;
